import type { ColDef, ISetFilterParams, ValueGetterParams } from 'ag-grid-community'

import { type Signal, SignalType } from '@/gen/inventory/v1/signal_pb'

import { distinctSorted } from '@/lib/string/string-utils'

import { TABLE_COLUMN_WIDTHS } from '@/components/lemmala-design-system/table/consts'
import { aiGovernanceFilterIcon } from '@/components/shared/ai-governance-icon/utils'

import { SIGNAL_TYPE_HEADER_NAME } from './labels'
import type { SignalTypeColumnOptions } from './types'

export const SIGNAL_TYPE_COLUMN_ID = 'signalType'

type NamedSignal = Pick<Signal, 'title' | 'subtitle' | 'signalType'>

// The titles map has no unspecified, legacy or uncategorized entry, so those show no type.
export const signalTypeTitleOf = (
  signal: Pick<Signal, 'signalType'> | undefined,
  titleByType: ReadonlyMap<SignalType, string>,
): string => (signal ? (titleByType.get(signal.signalType) ?? '') : '')

export type AlertNameParts = { title: string; description: string }

// The alert's type over its own description; an untyped alert keeps its own title instead.
export const alertNameParts = (
  signal: NamedSignal | undefined,
  titleByType: ReadonlyMap<SignalType, string>,
): AlertNameParts => {
  const title = signal?.title ?? ''
  const typeTitle = signalTypeTitleOf(signal, titleByType)
  if (!typeTitle) return { title, description: signal?.subtitle ?? '' }
  return { title: typeTitle, description: signal?.subtitle || (title === typeTitle ? '' : title) }
}

export const distinctSignalTypeTitles = (
  signals: readonly (Signal | undefined)[],
  titleByType: ReadonlyMap<SignalType, string>,
): string[] => distinctSorted(signals.map((s) => signalTypeTitleOf(s, titleByType)))

// getIcon for a set filter that lists signal type titles.
export const aiGovernanceTypeTitleIcon = (
  signals: readonly (Signal | undefined)[],
  titleByType: ReadonlyMap<SignalType, string>,
) =>
  aiGovernanceFilterIcon(
    new Set(signals.flatMap((s) => (s?.isAiGovernance ? [signalTypeTitleOf(s, titleByType)] : []))),
  )

export const signalTypeColumnDef = <TRow>({
  values,
  titleByType,
  getSignal,
  getIcon,
  minWidth = TABLE_COLUMN_WIDTHS.md,
}: SignalTypeColumnOptions<TRow>): ColDef<TRow> => ({
  colId: SIGNAL_TYPE_COLUMN_ID,
  headerName: SIGNAL_TYPE_HEADER_NAME,
  flex: 1,
  minWidth,
  sortable: true,
  filter: 'agSetColumnFilter',
  filterParams: {
    values,
    getIcon,
    suppressMiniFilter: true,
    selectAllOnMiniFilter: true,
    suppressSyncValuesAfterDataChange: true,
  } as ISetFilterParams,
  valueGetter: (params: ValueGetterParams<TRow>) =>
    signalTypeTitleOf(getSignal(params.data ?? undefined), titleByType),
})
